/**
 * Per-element CDM reuse, so rebuilding the player never has to give a
 * `<video>` a second ContentDecryptionModule — which Chrome forbids outright.
 *
 * ## The browser rule
 *
 * Once a media element has both a MediaKeys and a media player, Chrome
 * refuses to change or remove that CDM. BOTH of these reject:
 *
 *     video.setMediaKeys(null)          // remove
 *     video.setMediaKeys(otherKeys)     // replace
 *
 * with `InvalidStateError: Failed to execute 'setMediaKeys' on
 * 'HTMLMediaElement': The existing ContentDecryptionModule object cannot be
 * removed at this time.`
 *
 * Measured on Chromium 152 (`org.w3.clearkey`, secure context): removal is
 * allowed while the element has no `src`, and starts failing the moment a
 * MediaSource is attached — with `readyState` still 0, nothing buffered,
 * nothing played, and no encrypted media involved. So the bar is not "a
 * decoder holds the key"; it is "the element has a player". Every DRM rebuild
 * crosses it.
 *
 * ## Why hls.js's own mitigation cannot help
 *
 * hls.js serialises CDM teardown through a static `EMEController`
 * `CDMCleanupPromise`: the destroyed instance's `_clear()` calls
 * `setMediaKeys(null)` and the next instance waits on it before setting its
 * own keys. On Chrome that clear is exactly as forbidden as the set it is
 * meant to make room for. `_clear()` swallows the rejection as a NON-FATAL
 * `KEY_SYSTEM_DESTROY_MEDIA_KEYS_ERROR`, the cleanup promise resolves anyway,
 * and the new instance then fails its own `setMediaKeys` — that one fatally,
 * as `KEY_SYSTEM_NO_KEYS`. Ordering was never the problem, so no amount of
 * queueing fixes it. (Verified against the bundled 1.7.2: `_clear` at
 * `dist/hls.js:27580`, the swallowed catch at `:27604`.)
 *
 * ## What we do instead
 *
 * Hand every hls.js instance on a given element the SAME MediaKeys, via the
 * `requestMediaKeySystemAccessFunc` config hook. Nothing then tries to
 * replace the CDM:
 *
 *  - `EMEController.onMediaAttached` seeds `this.mediaKeys` from
 *    `media.mediaKeys` (`dist/hls.js:27568`), and `attemptSetMediaKeys`
 *    early-returns when the keys it is asked to set are already the element's
 *    (`:27026`) — so the forbidden call is never made; and
 *  - even if that early-return is missed, `setMediaKeys(sameObject)` is
 *    permitted by the browser (measured), so the fallback is benign.
 *
 * Two independent layers, which is what makes this safe to rest on across
 * hls.js upgrades: the first is an hls.js implementation detail, the second is
 * the platform's own behaviour.
 *
 * This is not a compromise on freshness. Once an element has a CDM, that CDM
 * is the only one it can ever have — the alternative is not "a fresh CDM", it
 * is "a fresh `<video>`", and the element belongs to the host application
 * (`createPlayer({ video })`), which may hold its own reference, listeners and
 * styling on it. Reuse is what lets a rebuild stay invisible to the host.
 *
 * What a rebuild actually needs is fresh *hls.js* state — the wedged
 * fragment pipeline, the orphaned key-load promise — not a fresh CDM, and it
 * still gets that. Fresh key SESSIONS also still work: closing a session and
 * opening another on a reused MediaKeys is permitted (measured), which is what
 * the license retry depends on.
 *
 * Scope: reuse covers keys this module created for the element. An element
 * that already carries a MediaKeys set by the host is not adopted — it is not
 * a case the SDK creates, and adopting a CDM whose key system we cannot
 * confirm would trade a clear error for a confusing one.
 */

/** Shape of the hls.js `requestMediaKeySystemAccessFunc` hook. */
type MediaKeyRequest = (
  keySystem: string,
  supportedConfigurations: MediaKeySystemConfiguration[],
) => Promise<MediaKeySystemAccess>;

/**
 * element → key system → its one MediaKeys, as a promise so concurrent
 * requests (hls.js can attempt several key systems) share a single creation.
 * Weak on the element so a discarded `<video>` takes its CDM entry with it.
 */
const keysByElement = new WeakMap<HTMLMediaElement, Map<string, Promise<MediaKeys>>>();

function mediaKeysFor(media: HTMLMediaElement, access: MediaKeySystemAccess): Promise<MediaKeys> {
  let bySystem = keysByElement.get(media);
  if (!bySystem) {
    bySystem = new Map();
    keysByElement.set(media, bySystem);
  }
  const cached = bySystem.get(access.keySystem);
  if (cached) return cached;
  // Evict on failure: a transient createMediaKeys() rejection must not pin a
  // permanently-rejected promise to the element for the rest of the page.
  const created = access.createMediaKeys().catch((err: unknown) => {
    if (bySystem.get(access.keySystem) === created) bySystem.delete(access.keySystem);
    throw err;
  });
  bySystem.set(access.keySystem, created);
  return created;
}

/**
 * Whether this page can request key-system access at all. False in a
 * non-secure context (EME is secure-context-only, so plain `http://` embeds
 * see no API), in a non-browser runtime, and in an embedder that strips it.
 *
 * The engine installs the hook below only when this holds, so that when it does
 * NOT hold hls.js falls back to its own null `requestMediaKeySystemAccessFunc`
 * and reports the cause — including the specific "not available over insecure
 * protocol" message (`dist/hls.js:26637`), which is the likeliest way an
 * integrator meets this. Installing our hook unconditionally would shadow that
 * diagnostic with an opaque TypeError.
 */
export function emeAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.requestMediaKeySystemAccess === 'function'
  );
}

/**
 * Video robustness rungs appended below whatever the caller asked for, weakest
 * last. Widevine's ladder continues to '' below this, and that rung is
 * deliberately absent: '' means "no requirement", which would turn a precise
 * `keySystemNoAccess` at access time into an opaque decrypt failure much later,
 * and no measured CDM has needed it. Audio is not laddered — the engine already
 * asks for the weakest named audio level (SW_SECURE_CRYPTO).
 */
const VIDEO_ROBUSTNESS_FALLBACKS = ['SW_SECURE_DECODE', 'SW_SECURE_CRYPTO'] as const;

type VideoRobustness = (typeof VIDEO_ROBUSTNESS_FALLBACKS)[number];

/** True for the Widevine key system, the only one whose robustness we ladder. */
function isWidevine(keySystem: string): boolean {
  return keySystem.startsWith('com.widevine');
}

/**
 * Expand each configuration into an ordered preference list: the caller's own
 * request first, then the same request at successively weaker VIDEO robustness.
 *
 * `requestMediaKeySystemAccess` takes candidates in descending preference and
 * returns the FIRST supported one, so this can only widen — a CDM that accepts
 * today's request still matches on entry 0 and is granted exactly what it is
 * granted now. It is the CDMs that accept NONE of our pins that change: they
 * get a weaker but working configuration instead of a hard rejection.
 *
 * Measured (Android 15 emulator, Chrome 124, Widevine L3, 2026-09-16): that
 * CDM supports video robustness SW_SECURE_CRYPTO and '' only. The engine's
 * SW_SECURE_DECODE — and every HW_* level — rejected with `NotSupportedError:
 * Unsupported keySystem or supportedConfigurations.`, which hls.js reports as a
 * fatal keySystemNoAccess and the SDK surfaces as an unplayable stream. With
 * the ladder the same call resolves at SW_SECURE_CRYPTO, keeping cbcs, and
 * createMediaKeys() succeeds.
 *
 * Exported for tests; the hook below is the only production caller.
 */
export function withVideoRobustnessFallbacks(
  keySystem: string,
  configurations: MediaKeySystemConfiguration[],
): MediaKeySystemConfiguration[] {
  if (!isWidevine(keySystem)) return configurations;

  /** True when `rung` is strictly weaker than every level this config asks for. */
  const weakens = (config: MediaKeySystemConfiguration, rung: VideoRobustness): boolean => {
    const video = config.videoCapabilities;
    // Nothing to weaken: a config that pins no video robustness already
    // imposes no video requirement, so a rung below it would be identical.
    if (!video?.length || !video.some((cap) => cap.robustness)) return false;
    return video.every((cap) => {
      const asked = VIDEO_ROBUSTNESS_FALLBACKS.indexOf(cap.robustness as VideoRobustness);
      // An unrecognised level (HW_*, or a future name) sorts above the whole
      // ladder: every rung here is weaker, so all of them are offered.
      return asked === -1 || VIDEO_ROBUSTNESS_FALLBACKS.indexOf(rung) > asked;
    });
  };

  // EVERY caller configuration comes before ANY weakening we synthesized.
  // Interleaving per-config (config, its rungs, next config, its rungs) would
  // let a weakened first configuration outrank a later one the caller asked
  // for outright: given [strictA, strictB] on a CDM that rejects strictA but
  // accepts both weakA and strictB, the browser returns the FIRST match — it
  // would pick weakA and silently drop to a lower protection level while the
  // caller's own strictB was available. Fallbacks are ours, not the caller's,
  // so they rank last; within them, rung-major order so the strongest
  // remaining protection is offered first across all configurations.
  const fallbacks = VIDEO_ROBUSTNESS_FALLBACKS.flatMap((robustness) =>
    configurations
      .filter((config) => weakens(config, robustness))
      .map((config) => ({
        ...config,
        videoCapabilities: config.videoCapabilities?.map((cap) => ({ ...cap, robustness })),
      })),
  );
  return [...configurations, ...fallbacks];
}

/**
 * Build the `requestMediaKeySystemAccessFunc` for an element: a real access
 * request (so the caller's `supportedConfigurations` are honoured and a
 * genuinely unsupported system still rejects), wrapped so `createMediaKeys()`
 * resolves to the element's one MediaKeys.
 */
export function reuseMediaKeysFor(media: HTMLMediaElement): MediaKeyRequest {
  return (keySystem, supportedConfigurations) => {
    // hls.js calls this hook and returns its result straight into a promise
    // chain, so a synchronous throw would escape its error handling. Keep the
    // contract the default hook has: always a promise, rejected on failure.
    if (!emeAvailable()) {
      return Promise.reject(
        new Error(
          'navigator.requestMediaKeySystemAccess is unavailable — EME needs a secure context (https, or localhost)',
        ),
      );
    }
    return navigator
      .requestMediaKeySystemAccess(
        keySystem,
        withVideoRobustnessFallbacks(keySystem, supportedConfigurations),
      )
      .then((access) => ({
        keySystem: access.keySystem,
        getConfiguration: () => access.getConfiguration(),
        createMediaKeys: () => mediaKeysFor(media, access),
      }));
  };
}

// Deliberately no way to drop an element's cached CDM. There is no correct
// caller: engine teardown runs on every rebuild, which is exactly when the
// entry must survive, and player teardown does not help either — if the host
// reuses the element for another player, that player still cannot be given a
// different CDM. The WeakMap covers the only case that matters, an element
// going out of scope.
